/**
 * AuditIntegrityBadge — public-facing "Audit Integrity Verified ✓"
 * indicator powered by the live `audit_chain` hash walk.
 *
 * Why this exists:
 *   Auditors + prospects want a one-glance signal that we haven't
 *   tampered with our own logs. This badge polls the public verify
 *   endpoint (no auth required, 60s server-side cache) and renders
 *   either a green-seal "verified" or a red-flag "anomaly" state.
 *
 * Endpoint: GET /api/security/audit/verify/public
 *   → { ok: bool, total: number, verified_at: ISO-string }
 *
 * Render guarantees:
 *   • If the endpoint 5xx's, we show the badge in a NEUTRAL state
 *     (no implication either way) — never a green tick on a stale fetch.
 *   • The total entry count is shown as evidence (more = more compelling).
 *   • The verified_at timestamp anchors the claim ("just verified" feels
 *     more credible than a static badge).
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, RefreshCw, Hash, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

function relativeTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AuditIntegrityBadge() {
  const [state, setState] = useState('loading'); // loading | ok | broken | error
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/security/audit/verify/public`, {
          credentials: 'omit',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;
        setData(json);
        setState(json.ok ? 'ok' : 'broken');
      } catch (e) {
        if (cancelled) return;
        setState('error');
      }
    };
    load();
    // Refresh every 2 minutes — server caches for 60s, so this just keeps
    // the badge looking "live" without thundering-herding the endpoint.
    const id = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const ok = state === 'ok';
  const broken = state === 'broken';
  const loading = state === 'loading';

  return (
    <div
      data-testid="audit-integrity-badge"
      className={`relative overflow-hidden rounded-lg border p-5 backdrop-blur transition-colors ${
        ok
          ? 'border-emerald-400/30 bg-emerald-500/[0.06]'
          : broken
            ? 'border-rose-400/30 bg-rose-500/[0.06]'
            : 'border-white/10 bg-white/[0.03]'
      }`}
    >
      {/* Pulse glow when verified */}
      {ok && (
        <div
          className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-30 pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(52,211,153,0.55) 0%, transparent 70%)',
            animation: 've-pulse 3s ease-in-out infinite',
          }}
        />
      )}
      <style>{`
        @keyframes ve-pulse {
          0%, 100% { opacity: 0.30; transform: scale(1); }
          50%      { opacity: 0.55; transform: scale(1.08); }
        }
      `}</style>

      <div className="flex items-start gap-3">
        <div
          className={`h-10 w-10 rounded-md flex items-center justify-center border shrink-0 ${
            ok
              ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-300'
              : broken
                ? 'bg-rose-500/20 border-rose-400/40 text-rose-300'
                : 'bg-white/5 border-white/15 text-white/55'
          }`}
        >
          {broken ? <ShieldAlert size={18} /> : <ShieldCheck size={18} />}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className={`text-[10px] uppercase tracking-[0.28em] font-bold ${
              ok
                ? 'text-emerald-300'
                : broken
                  ? 'text-rose-300'
                  : 'text-white/55'
            }`}
            data-testid="audit-badge-status"
          >
            [AUDIT INTEGRITY · {loading ? 'CHECKING…' : ok ? 'VERIFIED' : broken ? 'ANOMALY DETECTED' : 'OFFLINE'}]
          </div>
          <h3
            className="display text-xl mt-1.5 leading-tight text-[var(--ve-fg)]"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {ok && 'Tamper-evident log chain holds.'}
            {broken && 'Audit chain integrity violation.'}
            {loading && 'Verifying audit chain…'}
            {state === 'error' && 'Verification temporarily unavailable.'}
          </h3>
          <p className="mt-1.5 text-[12px] leading-relaxed opacity-75 text-[var(--ve-fg)]">
            {ok &&
              'Every privileged action is hash-chained to the next. Modify, insert, or delete any past entry and the chain breaks — visibly, on this page.'}
            {broken &&
              'A break in the chain has been detected. Our incident response team has been notified automatically.'}
            {loading && 'Walking the chain end-to-end to confirm integrity.'}
            {state === 'error' && 'The verification endpoint is briefly unreachable. The chain itself is unaffected.'}
          </p>
          {(ok || broken) && data && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[10px] font-mono opacity-65 text-[var(--ve-fg)]">
              <span className="inline-flex items-center gap-1">
                <Hash size={10} className="opacity-70" />
                <span data-testid="audit-badge-total">{Number(data.total).toLocaleString()}</span>
                <span className="opacity-70">entries</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock size={10} className="opacity-70" />
                verified {relativeTime(data.verified_at)}
              </span>
              <span className="inline-flex items-center gap-1 opacity-60">
                <RefreshCw size={10} className="opacity-60" />
                refresh · 2 min
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
